import { S as Swiper } from './swiper-core-BXF75Fx2.js';
import { N as Navigation } from './navigation-BvT7k6pb.js';
import { A as Autoplay } from './autoplay-CsPtyn3v.js';
import './create-element-if-not-defined-BFxMbgz9.js';

/**
 * Custom element for displaying customer testimonials as a Swiper carousel.
 * Initializes Swiper slider with responsive design matching Figma specs.
 */
class TestimonialSlider extends HTMLElement {
  /**
   * Lifecycle method called when the element is added to the DOM.
   */
  connectedCallback() {
    this.init();
  }

  /**
   * Initialize the testimonial slider.
   */
  init() {
    this.initSwiper();
  }

  /**
   * Get navigation configuration for Swiper.
   * @returns {Object}
   */
  getNavigationConfig() {
    const nextBtn = this.querySelector('.swiper-button-next');
    const prevBtn = this.querySelector('.swiper-button-prev');
    return {
      nextEl: nextBtn,
      prevEl: prevBtn,
    };
  }

  /**
   * Get autoplay configuration for Swiper.
   * @param {Element} swiperEl
   * @returns {Object|false}
   */
  getAutoplayConfig(swiperEl) {
    const autoplay = swiperEl?.dataset.autoplay === 'true';
    const delay = parseInt(swiperEl?.dataset.autoplayDelay) || 5000;

    if (!autoplay) return false;

    return {
      delay,
      disableOnInteraction: false,
      pauseOnMouseEnter: true,
    };
  }

  /**
   * Initialize Swiper for testimonial slider.
   * Responsive breakpoints: Mobile (1), Tablet (2), Desktop (3)
   */
  initSwiper() {
    const swiperEl = this.querySelector('.swiper--testimonial');

    if (!swiperEl) {
      console.warn('Testimonial slider: Swiper element not found');
      return;
    }

    const showNavigation = swiperEl?.dataset.showNavigation === 'true';
    const loop = swiperEl?.dataset.loop === 'true';

    const navigation = this.getNavigationConfig();
    const autoplay = this.getAutoplayConfig(swiperEl);

    const swiperOptions = {
      modules: [Navigation, Autoplay],
      spaceBetween: 16,
      slidesPerView: 1,
      watchOverflow: true,
      navigation: showNavigation ? navigation : false,
      autoplay,
      loop,
      breakpoints: {
        // Tablet: 2 slides
        768: {
          slidesPerView: 2.1,
        },
        // Desktop: 3 slides
        1024: {
          slidesPerView: 3.1,
        },
      },
      // Ensure equal height for all slides
      autoHeight: false,
      on: {
        init() {
          this.update();
        },
        resize() {
          this.update();
        },
      },
    };

    try {
      const swiper = new Swiper(swiperEl, swiperOptions);
      // Store swiper instance for potential future reference
      this.swiper = swiper;
    } catch (error) {
      console.error('Error initializing Testimonial Slider:', error);
    }
  }

  /**
   * Destroy the Swiper instance when element is removed.
   */
  disconnectedCallback() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }
}

customElements.define('testimonial-slider', TestimonialSlider);
